import { useEffect, useRef, useState } from "react";
import { ArrowRight, Check, Eye, EyeOff, LockKeyhole, ShieldCheck } from "lucide-react";
import { api, ApiClientError, type SessionData } from "./api";
import WorkspaceApp from "./WorkspaceApp";

type Screen = "loading" | "welcome" | "register" | "verify" | "sign-in" | "forgot" | "reset" | "setup" | "dashboard";

type Notice = { tone: "error" | "success" | "info"; message: string; requestId?: string } | null;

const launchParameters = new URLSearchParams(window.location.hash.replace(/^#/u, ""));
const invitedEmail = launchParameters.get("email") ?? "";
const invitedToken = launchParameters.get("invite") ?? "";
const linkedVerificationToken = launchParameters.get("verify") ?? "";
const linkedResetToken = launchParameters.get("reset") ?? "";

function Brand() {
  return <a className="brand" href="/" aria-label="SmartPaye home"><img src="/brand/mark-owner.svg" alt="" /><span>SmartPaye</span></a>;
}

function NoticeBox({ notice }: { notice: Notice }) {
  if (!notice) return null;
  return (
    <div className={`notice notice-${notice.tone}`} role={notice.tone === "error" ? "alert" : "status"}>
      <span>{notice.message}</span>
      {notice.requestId && <small>Request ID: {notice.requestId}</small>}
    </div>
  );
}

function ScreenHeading({ title, children }: { title: string; children?: React.ReactNode }) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    document.title = `${title} — SmartPaye`;
    headingRef.current?.focus({ preventScroll: true });
  }, [title]);

  return <h1 ref={headingRef} tabIndex={-1}>{children ?? title}</h1>;
}

function PasswordField(props: React.InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string }) {
  const [visible, setVisible] = useState(false);
  const { label, error, ...inputProps } = props;
  return (
    <label className="field">
      <span>{label}</span>
      <span className="password-control">
        <input {...inputProps} type={visible ? "text" : "password"} aria-invalid={Boolean(error)} />
        <button type="button" onClick={() => setVisible((value) => !value)} aria-label={visible ? "Hide password" : "Show password"}>
          {visible ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </span>
      {error && <small className="field-error">{error}</small>}
    </label>
  );
}

function App() {
  const [screen, setScreen] = useState<Screen>("loading");
  const [session, setSession] = useState<SessionData | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, setBusy] = useState(false);
  const [verificationToken, setVerificationToken] = useState("");
  const [resetToken, setResetToken] = useState("");

  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    api.session()
      .then((data) => {
        setSession(data);
        setScreen(data.businesses.length ? "dashboard" : "setup");
      })
      .catch(() => {
        if (linkedVerificationToken) {
          setVerificationToken(linkedVerificationToken);
          setScreen("verify");
        } else if (linkedResetToken) {
          setResetToken(linkedResetToken);
          setScreen("reset");
        } else {
          setScreen("welcome");
        }
      });
  }, []);

  const run = async (operation: () => Promise<void>) => {
    setBusy(true);
    setNotice(null);
    try {
      await operation();
    } catch (error) {
      if (error instanceof ApiClientError) {
        setNotice({ tone: "error", message: error.message, requestId: error.requestId });
      } else {
        setNotice({ tone: "error", message: "The service could not be reached. Your work was not submitted." });
      }
    } finally {
      setBusy(false);
    }
  };

  const refreshSession = async () => {
    const data = await api.session();
    setSession(data);
    setScreen(data.businesses.length ? "dashboard" : "setup");
  };

  if (screen === "dashboard" && session) {
    return <WorkspaceApp session={session} onSessionChanged={refreshSession} onSignedOut={() => { setSession(null); setScreen("welcome"); }} />;
  }

  return (
    <main className="page-shell">
      <section className="auth-panel">
        <header><Brand /><span className="phase-badge">Secure foundation</span></header>
        <div className="auth-content">
          {screen === "loading" && <div className="loading-state" aria-live="polite"><span className="spinner" /> Checking your secure session…</div>}

          {screen === "welcome" && (
            <div className="welcome">
              <span className="eyebrow">Built for Nigerian small businesses</span>
              <ScreenHeading title="PAYE, made clear.">PAYE,<br />made clear.</ScreenHeading>
              <p>Create a secure workspace for employee PAYE calculations and records you can explain.</p>
              <button className="button primary" onClick={() => setScreen("register")}>Create your workspace <ArrowRight size={18} /></button>
              <button className="text-button" onClick={() => setScreen("sign-in")}>Already have an account? Sign in</button>
              <ul className="trust-list">
                <li><Check size={15} /> Email verification before payroll access</li>
                <li><Check size={15} /> Every business isolated by membership</li>
                <li><Check size={15} /> Approval never means filing or remittance</li>
              </ul>
            </div>
          )}

          {screen === "register" && (
            <AuthForm title="Create your account" subtitle="Only the essentials for secure access." notice={notice}>
              <form onSubmit={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                void run(async () => {
                  const result = await api.register({
                    firstName: String(form.get("firstName")),
                    lastName: String(form.get("lastName")),
                    email: String(form.get("email")),
                    password: String(form.get("password")),
                    inviteToken: String(form.get("inviteToken")) || undefined
                  });
                  setVerificationToken(result.developmentVerificationToken ?? "");
                  setScreen("verify");
                  setNotice({ tone: "info", message: "Check your email to verify your account before signing in." });
                });
              }}>
                <div className="field-grid"><Field label="First name" name="firstName" autoComplete="given-name" required /><Field label="Last name" name="lastName" autoComplete="family-name" required /></div>
                <Field label="Work email" name="email" type="email" autoComplete="email" defaultValue={invitedEmail} required />
                <Field label="Pilot invitation code" name="inviteToken" defaultValue={invitedToken} autoComplete="off" />
                <p className="field-help">SmartPaye is currently onboarding invited pilot businesses.</p>
                <PasswordField label="Password" name="password" autoComplete="new-password" minLength={12} required />
                <p className="field-help">Use at least 12 characters with a letter and a number.</p>
                <SubmitButton busy={busy}>Create account</SubmitButton>
              </form>
              <button className="text-button" onClick={() => setScreen("sign-in")}>Sign in instead</button>
            </AuthForm>
          )}

          {screen === "verify" && (
            <AuthForm title="Verify your email" subtitle="Payroll access stays locked until email ownership is confirmed." notice={notice}>
              <form onSubmit={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                void run(async () => {
                  await api.verifyEmail(String(form.get("token")));
                  setScreen("sign-in");
                  setNotice({ tone: "success", message: "Email verified. You can now sign in." });
                });
              }}>
                <Field label="Verification token" name="token" defaultValue={verificationToken} autoComplete="one-time-code" required />
                {verificationToken && <p className="developer-note"><ShieldCheck size={16} /> Development preview token inserted locally. Production sends this by email.</p>}
                <SubmitButton busy={busy}>Verify email</SubmitButton>
              </form>
            </AuthForm>
          )}

          {screen === "sign-in" && (
            <AuthForm title="Welcome back" subtitle="Sign in to your SmartPaye workspace." notice={notice}>
              <form onSubmit={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                void run(async () => {
                  await api.signIn(String(form.get("email")), String(form.get("password")));
                  await refreshSession();
                });
              }}>
                <Field label="Work email" name="email" type="email" autoComplete="email" required />
                <PasswordField label="Password" name="password" autoComplete="current-password" required />
                <button className="inline-link" type="button" onClick={() => setScreen("forgot")}>Forgot password?</button>
                <SubmitButton busy={busy}>Sign in securely</SubmitButton>
              </form>
              <button className="text-button" onClick={() => setScreen("register")}>Create an account</button>
            </AuthForm>
          )}

          {screen === "forgot" && (
            <AuthForm title="Reset your password" subtitle="We’ll prepare instructions if the account is eligible." notice={notice}>
              <form onSubmit={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                void run(async () => {
                  const result = await api.forgotPassword(String(form.get("email")));
                  setResetToken(result.developmentResetToken ?? "");
                  setScreen("reset");
                  setNotice({ tone: "info", message: result.message });
                });
              }}>
                <Field label="Work email" name="email" type="email" autoComplete="email" required />
                <SubmitButton busy={busy}>Prepare reset instructions</SubmitButton>
              </form>
              <button className="text-button" onClick={() => setScreen("sign-in")}>Return to sign in</button>
            </AuthForm>
          )}

          {screen === "reset" && (
            <AuthForm title="Choose a new password" subtitle="All existing sessions will be revoked." notice={notice}>
              <form onSubmit={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                void run(async () => {
                  await api.resetPassword(String(form.get("token")), String(form.get("password")));
                  setScreen("sign-in");
                  setNotice({ tone: "success", message: "Password changed. Sign in with your new password." });
                });
              }}>
                <Field label="Reset token" name="token" defaultValue={resetToken} autoComplete="one-time-code" required />
                <PasswordField label="New password" name="password" autoComplete="new-password" minLength={12} required />
                <SubmitButton busy={busy}>Reset password</SubmitButton>
              </form>
            </AuthForm>
          )}

          {screen === "setup" && session && (
            <AuthForm title={`Welcome, ${session.user.firstName}.`} subtitle="Create the Nigerian business workspace that will own the PAYE records." notice={notice}>
              <form onSubmit={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                void run(async () => {
                  await api.createBusiness({ displayName: String(form.get("displayName")), legalName: String(form.get("legalName")) || undefined });
                  await refreshSession();
                });
              }}>
                <Field label="Business display name" name="displayName" autoComplete="organization" required />
                <Field label="Legal name (optional)" name="legalName" autoComplete="organization" />
                <div className="boundary-note"><LockKeyhole size={18} /><span>This workspace is isolated through your verified membership. Tax identifiers are not required at this stage.</span></div>
                <SubmitButton busy={busy}>Create secure workspace</SubmitButton>
              </form>
            </AuthForm>
          )}

        </div>
        <footer>Private pilot · PAYE preparation only · No filing or remittance</footer>
      </section>
      <aside className="visual-panel" aria-label="SmartPaye brand image">
        <img src="/brand/auth-hero.jpg" alt="A Nigerian business owner reviewing payroll records" />
        <div className="visual-quote"><span>SmartPaye principle</span><blockquote>“Know what was calculated, why it was calculated, and what to do next.”</blockquote></div>
      </aside>
    </main>
  );
}

function AuthForm({ title, subtitle, notice, children }: { title: string; subtitle: string; notice: Notice; children: React.ReactNode }) {
  return <div className="form-card"><span className="eyebrow">Secure account</span><ScreenHeading title={title} /><p>{subtitle}</p><NoticeBox notice={notice} />{children}</div>;
}

function Field({ label, ...props }: React.InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return <label className="field"><span>{label}</span><input {...props} /></label>;
}

function SubmitButton({ busy, children }: { busy: boolean; children: React.ReactNode }) {
  return <button className="button primary" type="submit" disabled={busy}>{busy ? <><span className="button-spinner" /> Working…</> : <>{children}<ArrowRight size={18} /></>}</button>;
}

export default App;
